import { sortBy, get } from 'lodash-es';
import moment from 'moment';
import { AssetModel, StatusSummary, DateRange } from './asset-details.state';
import * as EquipmentServiceUtil from '../../api/equipment.service.util';
import { lowerCaseKeysObject } from '../../utility/object.util.service';

//The list below are the names of datapoints in priority order
const RUN_HOURS_DATAPOINTS = [
  'unit_hour_meter',
  'engine_hour_meter',
  'eng_hrs_1',
  'eng_hrs_2',
  'ignition_run_hours',
  'run_hour',
  'run_time',
  'mach_hours',
];
const OPERATIONAL_HOURS = 'operating_hours';
const GPS_DATA_POINT = 'gps';
const FAULT_DATA_POINT = 'dtc';
const EXCLUDED_COMMANDS = ['communication_mode', 'set_accumulator'];

//function to build url for getting asset
export const BuildUri = (endpoint = '', equipmentId, includes = []) => {
  const includesUri = includes.map((include) => `include=${include}`).join('&');
  return `${endpoint}/${equipmentId}?${includesUri}`;
};

/**
 * Helper class to help make the transtion between api model and asset state model
 */

export class AssetModelHelper {
  static updateFromJsonApi(assetModel = {}, apiModel = {}, user = {}, includedTypes = []) {
    const { included = [], data = {} } = apiModel;
    const { id, attributes = {}, relationships = {} } = data;
    const { attrs = {}, communicatedAt, coords = [], createdAt, name } = attributes;
    const { datapointDisplay = {}, deviceCommands = {}, profileImage = {} } = relationships;

    const { datapointDescriptors, visibleDatapoints = [], permissions } = user;
    const userVisibleDatapointIds = visibleDatapoints.map((datapoint) => datapoint.id);

    const asset = assetModel.id === id ? { ...assetModel } : new AssetModel();
    asset.id = id;
    asset.name = name;
    asset.createdAt = createdAt;
    asset.showLocation = userVisibleDatapointIds.includes(GPS_DATA_POINT);
    asset.coords = asset.showLocation ? coords : null;
    asset.communicatedAt = communicatedAt;

    const normalizedAttrs = lowerCaseKeysObject(attrs);
    asset.make = normalizedAttrs.make;
    asset.model = normalizedAttrs.model;
    asset.year = normalizedAttrs['model year'];
    asset.esn = normalizedAttrs['onei3:serial'];

    asset.datapointDisplay = datapointDisplay;
    asset.attrs = attrs;
    asset.networkTechnology = attributes.networkTechnology;

    const flattenIncluded = this.flattenIncludedObjects(included);
    if (includedTypes.includes('images')) {
      asset.images = this.getIncludedType('image', flattenIncluded);
      const { data: imageData = {} } = profileImage;
      asset.profileImage = this.getProfileImg(asset.images, imageData.id);
    }
    [asset.installation] = this.getIncludedType('installation', flattenIncluded);
    [asset.device] = this.getIncludedType('device', flattenIncluded);

    const [includedPackages] = this.getIncludedType('package', flattenIncluded);
    const { packages = [] } = includedPackages || {};
    asset.packages = packages;
    asset.syncRunHours = this.getSyncRunHours(packages);

    if (includedTypes.includes('maintenancePlans')) {
      const allMaintenancePlans = this.getIncludedType('maintenancePlan', flattenIncluded);
      const { maintenanceStatus, maintenancePlans } = this.getMaintenance(allMaintenancePlans);
      asset.maintenancePlans = maintenancePlans;
      asset.maintenanceStatus = maintenanceStatus;
    }
    if (includedTypes.includes('users')) {
      asset.users = this.getIncludedType('user', flattenIncluded);
    }
    if (includedTypes.includes('deviceCommands')) {
      asset.deviceCommands = deviceCommands.data || [];
      asset.canPollData = this.isPollCommandAvailable(asset.deviceCommands, permissions);
      asset.hasSetAccumulatorCommand = !!asset.deviceCommands.find(
        (deviceCommand) => deviceCommand.id === 'set_accumulator',
      );

      //show command tab for command type control or configure with some excptions
      asset.hasCommandsToDisplay =
        asset.deviceCommands.filter(
          (command) =>
            (command.commandType === 'control' || command.commandType === 'configure') &&
            !EXCLUDED_COMMANDS.includes(command.id),
        ).length > 0;
    }
    if (includedTypes.includes('series')) {
      const includedSeries = this.getIncludedType('series', flattenIncluded);
      const orderedSeriesList = this.applyDataPointDisplay(includedSeries, datapointDisplay);
      const { series, gpsSeries } = this.filterSeries(orderedSeriesList);
      asset.series = series;
      asset.locationSeries = gpsSeries;
      asset.defaultChartedseriesIds = series
        .filter((ser) => ser.keyMetric && ser.communicatedIn_30d)
        .map((s) => s.id);
    }
    if (includedTypes.includes('faults')) {
      const faultSeries = this.getIncludedType('fault', flattenIncluded);
      asset.faults = this.getActivFaultsAndApplyDecsriptors(faultSeries, datapointDescriptors);
    }
    if (includedTypes.includes('alerts')) {
      const includedAlerts = this.getIncludedType('alertOccurence', flattenIncluded);
      const { validAlerts = [], alertsStatus } = this.validateAlertsAndApplyDescriptor(
        includedAlerts,
        userVisibleDatapointIds,
        datapointDescriptors,
      );
      asset.alerts = validAlerts;
      asset.alertStatus = alertsStatus;
    }
    if (includedTypes.includes('attributes')) {
      const includedAttributes = this.getIncludedType('attribute', flattenIncluded);
      asset.attributes = this.buildAttrsAsAttributesObject(attrs, includedAttributes);
    }

    return asset;
  }

  /**
   * flatten nested attributes object in included
   * @param {*} includedList
   */
  static flattenIncludedObjects(includedList = []) {
    return includedList.map((included) => {
      const { attributes, ...rest } = included;
      return { ...rest, ...attributes };
    });
  }
  static getIncludedType(includedType, includedList = []) {
    return includedList.filter((included) => included.type === includedType);
  }
  static getProfileImg(images = [], profileImageId) {
    return images.find((e) => e.id === profileImageId);
  }
  static isPollCommandAvailable(deviceCommands = [], userPermissions = []) {
    return (
      !!deviceCommands.find((deviceCommand) => deviceCommand.id === 'poll') &&
      userPermissions.includes('commands.query')
    );
  }

  /**
   * return an object for fault series, gps data point,and other series
   * find run hours series and tag it with chosenRunHours
   * @param {*} seriesList
   */
  static filterSeries(seriesList = []) {
    const foundRunHoursDataPoints = {};
    const seriesWithoutGps = [];
    const faultSeries = [];
    let gpsSeries = null;
    let operatingHours = null;

    seriesList.forEach((series) => {
      const { dataId, descriptor = {}, attrs = {}, last = null } = series;
      //override series title with descriptor one
      if (descriptor.title) {
        series.title = descriptor.title;
      }
      if (last) {
        //set flag on the series indecate that series has recent data(within 30 day)
        series.communicatedIn_30d = DateRangeHelper.isDateWithinThirtyDayfromToday(last.t);
      }

      //if series has attr(onei3:dp_std=operating_hours) then it will be the chosen run hours
      if (attrs && attrs['onei3:dp_std'] === OPERATIONAL_HOURS) {
        operatingHours = series;
      }
      const indexOfRunHours = RUN_HOURS_DATAPOINTS.indexOf(dataId);
      if (indexOfRunHours !== -1) {
        foundRunHoursDataPoints[indexOfRunHours] = series;
      }
      if (dataId === GPS_DATA_POINT) {
        gpsSeries = { ...series };
      } else if (dataId === FAULT_DATA_POINT) {
        faultSeries.push(series);
      } else {
        seriesWithoutGps.push(series);
      }
    });

    //if we did not find operatingHours by attr, try to find it by the predefind list
    if (operatingHours === null) {
      //get lowest index as it has high priorty based on the RUN_HOURS_DATAPOINTS list above
      const [lowestIndex = -1] = Object.keys(foundRunHoursDataPoints).sort();
      operatingHours = lowestIndex > -1 ? foundRunHoursDataPoints[lowestIndex] : null;
    }
    //tag chosenOperatingHours seriers with chosenRunHours prop
    if (operatingHours) {
      const runhourindex = seriesWithoutGps.findIndex((series) => series.id === operatingHours.id);
      if (runhourindex !== -1) {
        seriesWithoutGps[runhourindex].chosenRunHours = true;
      }
    }

    return { series: seriesWithoutGps, gpsSeries: gpsSeries, faultSeries: faultSeries };
  }
  static applyDataPointDisplay(seriesList = [], datapointDisplay = {}) {
    const displayItems = get(datapointDisplay, 'data.attributes.displayItems', []);
    if (displayItems.length > 0) {
      seriesList.forEach((series) => {
        const matchingDisplayItem = displayItems.find((item) => item.dataId === series.dataId);
        if (matchingDisplayItem) {
          series.display = matchingDisplayItem.visible;
          series.order = matchingDisplayItem.ordinal;
          series.keyMetric = matchingDisplayItem.keyMetric;
          series.decimals = matchingDisplayItem.decimals;
          series.displayItem = matchingDisplayItem;
        } else {
          series.display = true;
          series.order = 999;
          series.keyMetric = false;
          series.decimals = 2;
        }
      });
    }
    //order the array by order
    const orderedseriesList = sortBy(seriesList, (series) => series.order);

    return orderedseriesList;
  }

  /**
   * create fault code then override the fault seriers title
   * @param {*} faultSeries dtc seriers
   * @param {*} descriptors
   */
  static setFaultDtcDescription(faultSeries, descriptors) {
    if (faultSeries) {
      faultSeries.dtcFaultDescriptor = EquipmentServiceUtil.findFaultDtcDescriptor(
        faultSeries,
        descriptors,
      );
      faultSeries.title = EquipmentServiceUtil.formatFaultCode(faultSeries) || faultSeries.title;
    }
    return faultSeries;
  }

  /**
   * filter the list based on user visible data points and create fault code for dtc ones
   * @param {*} alertsList
   * @param {*} userVisibleDatapoint
   * @param {*} descriptors
   */
  static validateAlertsAndApplyDescriptor(
    alertsList = [],
    userVisibleDatapointIds = [],
    descriptors,
  ) {
    const activeHighAlerts = [];
    const activeMeduimAlerts = [];
    const activeLowAlerts = [];
    const validAlerts = [];
    alertsList.forEach((alert) => {
      const { series = {}, priority = '', state = '' } = alert;
      const alertPriority = priority.toLowerCase();
      const alertState = state.toLowerCase();
      //assuming the datapoint id is first element in tags array
      const [datapintId] = series.tags;
      if (userVisibleDatapointIds.includes(datapintId)) {
        //set code format and descriptor for fault alerts
        if (datapintId === FAULT_DATA_POINT) {
          alert.series = this.setFaultDtcDescription(series, descriptors);
          if (alert.series.dtcFaultDescriptor && alert.series.dtcFaultDescriptor.title) {
            alert.series.faultDescription = alert.series.dtcFaultDescriptor.title;
          }
        }
        validAlerts.push(alert);
        //to calculate the overall status on active alert only not returned
        if (alertState === 'active') {
          //filter alerts based on priority
          if (alertPriority === 'high') {
            activeHighAlerts.push(alert);
          } else if (alertPriority === 'medium') {
            activeMeduimAlerts.push(alert);
          } else {
            activeLowAlerts.push(alert);
          }
        }
      }
    });
    const activeAlerts = [...activeHighAlerts, ...activeMeduimAlerts, ...activeLowAlerts];

    //status is the heighest alert Priority found
    let alertsStatus = new StatusSummary(activeAlerts.length);
    if (activeHighAlerts.length > 0) {
      alertsStatus.setStatus('high');
    } else if (activeMeduimAlerts.length > 0) {
      alertsStatus.setStatus('medium');
    } else if (activeLowAlerts.length > 0) {
      alertsStatus.setStatus('low');
    }

    return { validAlerts, alertsStatus };
  }

  /**
   * retrun active fauts (value =1) then format fault code and get fault description
   * @param {*} faultsSeriesList
   * @param {*} descriptors
   */
  static getActivFaultsAndApplyDecsriptors(faultsSeriesList = [], descriptors) {
    return faultsSeriesList
      .filter(({ last }) => last && last.v)
      .map((fault) => this.setFaultDtcDescription(fault, descriptors));
  }

  static getMaintenance(maintenancePlans = []) {
    const dueMaintenance = [];
    const comingMaintenance = [];
    maintenancePlans.forEach((item) => {
      const { meta, ...rest } = item;
      item = { ...rest, ...meta };
      const statusCode = get(item, 'status.code', '').toLowerCase();
      if (statusCode === 'due') {
        dueMaintenance.push(item);
      } else if (statusCode === 'upcoming') {
        comingMaintenance.push(item);
      }
    });

    const activeMaitenance = [...dueMaintenance, ...comingMaintenance];

    const maintenanceStatus = new StatusSummary(activeMaitenance.length, 'no_maintenance');
    if (dueMaintenance.length > 0) {
      maintenanceStatus.setStatus('due');
    } else if (comingMaintenance.length > 0) {
      maintenanceStatus.setStatus('upcoming');
    }
    return { maintenanceStatus, maintenancePlans: activeMaitenance };
  }

  static buildAttrsAsAttributesObject(assetAttrs = {}, attributes = []) {
    let indexedAttributes = attributes.reduce((indexed, attribute) => {
      const key = attribute.key.toLowerCase();
      indexed[key] = attribute;
      return indexed;
    }, {});

    const assetAttributes = Object.keys(assetAttrs)
      .filter((key) => key && key.indexOf('onei3:') === -1)
      .map((attrKey) => ({
        key: attrKey,
        value: assetAttrs[attrKey],
        dataType: indexedAttributes[attrKey.toLowerCase()]
          ? indexedAttributes[attrKey.toLowerCase()].dataType
          : null,
      }));

    return assetAttributes;
  }

  static validateAssetDataIntegrity(returndSeries = [], assetSeries = []) {
    const isAssetDataUptodate = returndSeries
      .map((item) => item.series)
      .every((series) => {
        const seriesInAssetObject = assetSeries.find((s) => s.id === series.id) || {};
        if (series.last || seriesInAssetObject.last) {
          return series.last.t === seriesInAssetObject.last.t;
        }
        return true;
      });
    return isAssetDataUptodate;
  }

  /**
   * return data point id that allow sync run hours
   */
  static getSyncRunHours(packages = [], device = {}) {
    //  const m5device = (device.attrs.model || '').toLowerCase().startsWith('m5s');
    const pkgWithSyncDatapoint = packages.find((pkg) => pkg.allowsRunHoursSync);
    if (pkgWithSyncDatapoint) {
      return pkgWithSyncDatapoint.runHoursSync;
    }
    return null;
  }

  static addOrUpdateSeriesData(source, series, data = [], datafor, rollup) {
    const dataOjectForcurrentSeries = source[series.id] ? source[series.id] : {};
    switch (datafor) {
      case 'chart': {
        dataOjectForcurrentSeries.series = series;
        dataOjectForcurrentSeries.chartRollup = rollup;
        dataOjectForcurrentSeries.chartData = [...data];
        break;
      }
      case 'values': {
        dataOjectForcurrentSeries.series = series;
        dataOjectForcurrentSeries.valuesData = [...data];
        break;
      }
      default: {
        dataOjectForcurrentSeries.series = series;
        dataOjectForcurrentSeries.valuesData = [...data];
        dataOjectForcurrentSeries.chartData = [...data];
        dataOjectForcurrentSeries.chartRollup = rollup;
      }
    }
    source[series.id] = dataOjectForcurrentSeries;
    return source;
  }
}

//date  rage helper
export class DateRangeHelper {
  static validOptions() {
    return [1, 7, 30];
  }

  static setByOption(opt) {
    const option = this.validOptions().includes(opt) ? opt : 1;
    //capture the current moment so we calculate start and end from the same date
    const now = moment();
    const start = moment(now).subtract(opt, 'days').startOf('minute').toDate();
    const end = moment(now).endOf('minute').toDate();

    return new DateRange(start, end, option);
  }

  static setByRange(start, end) {
    const endDate = moment(end).endOf('minute');
    //if end date less than one minute from now, than range maybe will match one of the options
    const endDateMinFromNow = moment().diff(endDate, 'minutes', true);
    const startDate = moment(start).startOf('minute');
    const rangDiffIndays = endDate.diff(startDate, 'days', true);
    const isRangeMatchOption =
      this.validOptions().includes(rangDiffIndays) && endDateMinFromNow < 1;
    const option = isRangeMatchOption ? rangDiffIndays : undefined;
    return new DateRange(startDate.toDate(), endDate.toDate(), option);
  }

  static getRangeAggregationInterval(start, end) {
    //find date rang diff in days
    //between 2 and 8 day will need aggregation by 1 hour
    //more than 8 days need aggregation by 1 day
    //less than 2 day no aggregation requird(raw data)
    const diffInDays = moment(end).diff(moment(start), 'days', true);
    if (diffInDays > 2 && diffInDays < 8) {
      return '1hour';
    }
    if (diffInDays >= 8) {
      return '1day';
    }
    return null;
  }

  static isDateWithinThirtyDayfromToday(date) {
    const diffFromToday = moment().diff(moment(date), 'days', true);
    return diffFromToday <= 30;
  }
}
